import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Stack, Text, Textarea } from '@mantine/core';
import { useRidesBookingsService } from '@actschurch/ccms-sdk';
import { useCopy, supportLink } from '@/lib/settings';
import { PickerCard, PickerGroup } from '@/components/ui/PickerCard';
import { Callout } from '@/components/ui/Callout';
import { ErrorAlert } from '@/components/rides/ErrorAlert';
import { ConfirmSheet } from './ConfirmSheet';

const REASONS = [
  { title: "My driver didn't come", hint: 'They never arrived at the campus.' },
  { title: "I couldn't find my driver", hint: 'We missed each other at the campus.' },
  { title: 'Something made me feel unsafe', hint: 'During the lift or at the campus.' },
  { title: 'Something else', hint: 'Tell us below.' },
];

interface Props {
  opened: boolean;
  onClose: () => void;
  bookingId: number;
  /** Called once the report has been sent. */
  onSent?: () => void;
}

/**
 * "Report a problem" with a booking (POST bookings/{id}/dispute). The terms promise members a way
 * to raise a dispute; this is it. Plain reasons to pick from, an optional description and the
 * support contact.
 */
export function ReportProblemSheet({ opened, onClose, bookingId, onSent }: Props) {
  const { t, settings } = useCopy();
  const bookings = useRidesBookingsService();
  const [reason, setReason] = useState<string | null>(null);
  const [details, setDetails] = useState('');
  const [tried, setTried] = useState(false);
  const support = supportLink(settings);

  const send = useMutation({
    mutationFn: () => bookings.dispute(bookingId, { reason: reason!, details: details.trim() || null }),
    onSuccess: () => onSent?.(),
  });

  useEffect(() => {
    if (!opened) return;
    setTried(false);
    if (send.isSuccess) {
      setReason(null);
      setDetails('');
      send.reset();
    }
    // Reset only when the sheet opens again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const needDetails = reason === 'Something else' && details.trim().length < 5;
  const submit = () => {
    setTried(true);
    if (!reason || needDetails) return;
    send.mutate();
  };

  return (
    <ConfirmSheet opened={opened} onClose={onClose} title={send.isSuccess ? 'Report sent' : 'Report a problem'}>
      {send.isSuccess ? (
        <Stack gap="md">
          <Callout tone="ok" live>{t('report.sent')}</Callout>
          <Button fullWidth onClick={onClose}>Done</Button>
        </Stack>
      ) : (
        <Stack gap="md">
          <Text>{t('report.intro')}</Text>
          <Callout tone="warn">{t('report.emergency')}</Callout>
          <PickerGroup ariaLabel="What happened?">
            {REASONS.map((r) => (
              <PickerCard key={r.title} selected={reason === r.title} onSelect={() => setReason(r.title)} title={r.title} description={r.hint} />
            ))}
          </PickerGroup>
          {tried && !reason && <Text c="var(--rides-bad)" fw={600} role="alert">Please choose what happened.</Text>}
          <Textarea
            label="What happened?"
            description={reason === 'Something else' ? 'Please tell us a little about it.' : 'Optional. Add anything that will help us.'}
            value={details}
            onChange={(e) => setDetails(e.currentTarget.value)}
            autosize
            minRows={3}
            maxLength={1000}
            error={tried && needDetails ? 'Please tell us what happened.' : undefined}
          />
          <ErrorAlert error={send.error} title="We couldn't send your report" />
          <Button fullWidth loading={send.isPending} onClick={submit}>Send report</Button>
          <Button fullWidth variant="default" onClick={onClose}>Cancel</Button>
          {support && <Button fullWidth variant="subtle" component="a" href={support.href}>Contact the Carpool team</Button>}
        </Stack>
      )}
    </ConfirmSheet>
  );
}
